import { useEffect } from 'react'

/**
 * NavActive - marks the header nav entry that matches the page you are on.
 *
 * The header in Pentabay.html is static: it mounts once and survives every
 * client-side navigation, so nothing in it knows which page is open. This
 * component watches the same signals Breadcrumbs.jsx does (<html data-route>,
 * popstate, hashchange, pb-pages-mounted) and, on each route change, stamps:
 *
 *   .pb-nav-current        on every header <a> whose href is the open route
 *                          (desktop dropdown item + the mobile panel twin),
 *                          plus aria-current="page" so screen readers get the
 *                          same signal the pill gives sighted visitors.
 *   .pb-nav-group-current  on the <summary> of the dropdown that holds it -
 *                          the closed-state "you are in here" ring.
 *
 * All the visual treatment lives in CSS: the top-level pill in Pentabay.html
 * (nav section) and the mobile panel rows in StyleOverrides.jsx.
 *
 * Groups are resolved structurally, never by summary text - TranslationManager
 * rewrites those labels, so "find the details whose summary says Services"
 * would silently stop matching the moment a visitor switches language. Instead
 * a route with no <a> of its own (the /services hub, a blog article) names a
 * PROBE href that only its own dropdown contains, and we mark whichever
 * <details> holds that probe.
 */

// Routes the nav has no link for. `link` highlights an existing item (a blog
// article lights up "Blog"); `probe` names a member href used only to identify
// the owning dropdown when no item should light up at all (hub pages).
const FALLBACK = {
  '/services':      { probe: '/custom-software' },
  /* The five child services are absent from the dropdown by design (it lists
     parents only - see the Services <details> in Pentabay.html). `link`
     rather than `probe` so the PARENT row lights up while you are on a
     child, which is the same treatment a blog article gets for "Blog":
     it tells you where you are in the tree instead of highlighting nothing. */
  '/seo-services':              { link: '/digital-marketing' },
  '/ppc-google-ads':            { link: '/digital-marketing' },
  '/industries':    { probe: '/healthcare' },
  // Blog hub + every article: the trail is Company → Blog, so "Blog" is the
  // item that lights up and Company is the group ring.
  '/insights':                                              { link: '/blog' },
  '/healthcare-software-development-guide':                  { link: '/blog' },
  '/fhir-hl7-integration-guide':                            { link: '/blog' },
  '/custom-software-development-cost-canada':               { link: '/blog' },
  '/soc-2-ready-vendor-guide':                              { link: '/blog' },
  '/public-sector-software-buyers':                         { link: '/blog' },
  '/low-code-no-code-when-it-actually-works':               { link: '/blog' },
  '/cloud-devops-what-it-actually-includes':                { link: '/blog' },
  '/enterprise-it-legacy-modernization':                    { link: '/blog' },
  '/ai-edtech-student-data-privacy-ferpa-coppa':             { link: '/blog' },
  '/real-estate-proptech-what-software-actually-needs-to-do': { link: '/blog' },
  '/b2b-saas-what-enterprise-buyers-actually-check':          { link: '/blog' },
  '/banking-lending-software-core-system-integration':        { link: '/blog' },
  '/mobile-development-native-vs-cross-platform':           { link: '/blog' },
  '/web-marketing-sites-what-actually-matters':             { link: '/blog' },
  '/how-to-choose-a-software-development-company':          { link: '/blog' },
  '/custom-software-vs-off-the-shelf':                      { link: '/blog' },
  '/offshore-vs-nearshore-software-development':            { link: '/blog' },
  '/healthcare-software-vendor-checklist':                  { link: '/blog' },
  '/phipa-vs-hipaa-canadian-healthcare-software-compliance': { link: '/blog' },
  '/ai-data-compliant-ai-canada':                           { link: '/blog' },
  '/product-design-compliance-decision-aoda':               { link: '/blog' },
  '/population-health-management-what-actually-matters':    { link: '/blog' },
  '/sdoh-data-integration-what-actually-matters':            { link: '/blog' },
  '/ransomware-as-a-service-why-skill-barrier-dropped':      { link: '/blog' },
}

// Resolve the active route: clean /path first, legacy hash fragment as a
// fallback (matches currentRoute() in Breadcrumbs.jsx / RouteSEO.jsx).
function currentRoute() {
  const path = window.location.pathname || '/'
  const m = path.match(/^\/([a-z0-9-]+)\/?$/i)
  if (m && m[1]) return '/' + m[1]
  const raw = window.location.hash || ''
  if (!raw || raw === '#' || raw === '#/') return '/'
  let hp = raw.startsWith('#') ? raw.slice(1) : raw
  if (!hp.startsWith('/')) hp = '/' + hp
  const idx = hp.indexOf('#')
  if (idx !== -1) hp = hp.slice(0, idx)
  return hp || '/'
}

// Route an internal href points at, or null for the product sub-sites and
// anything else that leaves the SPA (https://…, mailto:, tel:).
function routeOf(a) {
  const href = a.getAttribute('href') || ''
  if (!href.startsWith('/') || href.startsWith('//')) return null
  const p = href.split('#')[0].replace(/\/+$/, '')
  return p || '/'
}

// Every dropdown a link belongs to, innermost first - the outer hamburger
// <details> is skipped (its own top-level links like Compliance/Contact sit
// directly inside it, and ringing the hamburger for those says nothing
// useful). Plural because 2026-09-21 added a second nesting level (Digital
// Marketing Services / Cybersecurity Services inside the Services dropdown,
// desktop .pb-flyout and mobile .pb-nav-sub): a link inside one of those now
// has TWO ancestor <details> - its own flyout and the outer Services panel -
// and both should ring, the same way a visitor on /seo-services benefits
// from seeing both "Services" and "Digital Marketing Services" marked, not
// just the innermost one.
function groupsOf(a) {
  const out = []
  let d = a.closest('details')
  while (d) {
    if (!d.classList.contains('pb-nav-mobile')) out.push(d)
    d = d.parentElement ? d.parentElement.closest('details') : null
  }
  return out
}

function apply() {
  const header = document.getElementById('nav')
  if (!header) return

  header.querySelectorAll('.pb-nav-current, .pb-nav-group-current').forEach((el) => {
    el.classList.remove('pb-nav-current', 'pb-nav-group-current')
    if (el.getAttribute('aria-current') === 'page') el.removeAttribute('aria-current')
  })

  const route = currentRoute()
  if (route === '/') return // the logo is the home link; no pill to light up

  const fb = FALLBACK[route] || {}
  // Menu links only - the two navs, not the header's other /contact-us links.
  // The "Talk to us" CTA points at /contact-us too, and marking a call to
  // action as the current page helps nobody: it would announce aria-current on
  // a button whose whole job is to look like a button.
  const links = [...header.querySelectorAll('nav a[href], .pb-nav-mobile-panel a[href], .pbf-mob a[href]')]
  const groups = new Set()

  // The item pill: an exact-route match, or the item a fallback delegates to.
  const itemRoute = fb.link || (fb.probe ? null : route)
  if (itemRoute) {
    links.filter((a) => routeOf(a) === itemRoute).forEach((a) => {
      a.classList.add('pb-nav-current')
      if (itemRoute === route) a.setAttribute('aria-current', 'page')
      groupsOf(a).forEach((g) => groups.add(g))
    })
  }

  // Hub pages light up their dropdown(s) only, located via the probe href.
  if (fb.probe) {
    links.filter((a) => routeOf(a) === fb.probe).forEach((a) => {
      groupsOf(a).forEach((g) => groups.add(g))
    })
  }

  groups.forEach((d) => {
    const s = d.querySelector(':scope > summary')
    if (s) s.classList.add('pb-nav-group-current')
  })

  // The shared frame header (frame/header.html) has no <details>: each
  // top-level item is an a.pbf-l inside a .pbf-has-dd with its panel beside
  // it. Light up that top-level item whenever the open page is (or probes to)
  // one of its panel's links, e.g. "Industries" while on /healthcare.
  const owner = (a) => a.closest('.pbf-has-dd')?.querySelector(':scope > a.pbf-l')
  const marked = new Set()
  if (itemRoute) links.filter((a) => routeOf(a) === itemRoute).forEach((a) => marked.add(owner(a)))
  if (fb.probe) links.filter((a) => routeOf(a) === fb.probe).forEach((a) => marked.add(owner(a)))
  marked.forEach((top) => top && top.classList.add('pb-nav-current'))
}

export default function NavActive() {
  useEffect(() => {
    apply()
    const mo = new MutationObserver(apply)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-route'] })
    window.addEventListener('popstate', apply)
    window.addEventListener('hashchange', apply)
    window.addEventListener('pb-pages-mounted', apply)
    return () => {
      mo.disconnect()
      window.removeEventListener('popstate', apply)
      window.removeEventListener('hashchange', apply)
      window.removeEventListener('pb-pages-mounted', apply)
    }
  }, [])
  return null
}
